import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Route, Switch, RouteComponentProps, Redirect } from 'react-router-dom';
import {
  Box,
  Typography,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogActions,
  makeStyles,
} from '@material-ui/core';
import { format } from 'date-fns';

import { closeNav, getIsNavOpen, toggleNav, ttDate } from '../state/contexts/app';

import { ismVoult, mvoultLogo } from '../config';
import { featureFlags } from '../config/featureFlags';

import { MainNavigation } from './modules/main/MainNavigation';
import AllowForRoles from './modules/auth/AllowForRoles';
import { MainToolbar } from './modules/main/MainToolbar';
import { DashboardPageLoader } from './modules/dashboard/DashboardPageLoader';
import { AlarmsPageLoader } from './modules/alarm-page/AlarmsPageLoader';
import { ReportsPageLoader } from './modules/reports/ReportsPageLoader';
import { UserPageLoader } from './modules/user-page/UserPageLoader';
import { AlertPageLoader } from './modules/alarm-notifications/AlertPageLoader';
import { ControlSummaryPageLoader } from './modules/control-summary/ControlSummaryPageLoader';
import { AnalysisPageLoader } from './modules/analysis-page/AnalysisPageLoader';
import { OpSummaryPageLoader } from './modules/op-summary/OpSummaryPageLoader';
import EquipmentPageLoader from './modules/equipment/EquipmentPageLoader';
import InstallerPageLoader from './modules/installer/InstallerPageLoader';
import { PrimaryButton, SecondaryButton } from './ui/Buttons';
import Settings from './modules/settings';
import Commands from './modules/commands';
import { ErrorMessage } from './ui/ErrorMessage';
import PointsPageLoader from './modules/points/PointsPageLoader';

import { PathBuilder, PathPattern, SiteRouteMatch } from '../routing';

import { SiteRole, UserProfile } from '../models';

import { useAction } from '../helpers/hooks';
import { getSiteContext } from 'src/state/contexts/site';
import { FORTRESS_GUARDIAN } from 'src/data/constants';
import { getPoints } from 'src/state/entities/points/actions';

const TimeTravelBanner = (props: { className: string }) =>
  ttDate?.date ? (
    <div {...props}>
      <Typography variant="body2">
        Currently in time travel mode at {format(new Date(ttDate.date), 'M/d/yyyy, h:mm a')}
      </Typography>
    </div>
  ) : null;

const NotAuthorized = () => (
  <div style={{ padding: 36 }}>
    <ErrorMessage message="Not authorized to view this page." />
  </div>
);

const SameCustomerMessage = () => (
  <div style={{ padding: 36 }}>
    <ErrorMessage message="Notification management only allowed for same-customer users." />
  </div>
);

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
  },
  content: {
    flexGrow: 1,
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
  },
  contentOuter: {
    margin: 0,
    flex: 1,
    overflow: 'hidden',
    position: 'relative',
  },
  contentInner: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    overflowY: 'auto',
  },
  timeTravel: {
    position: 'fixed',
    backgroundColor: '#5e35b1',
    color: theme.palette.common.white,
    padding: '0 6px',
    borderBottomLeftRadius: 3,
    borderBottomRightRadius: 3,
    zIndex: 1500,
    top: 0,
    left: '50%',
    transform: 'translate(-50%, 0)',
  },
  dialog: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    textAlign: 'center',
  },
}));

const SiteRoute = ({
  route,
  profile,
}: {
  route: RouteComponentProps<SiteRouteMatch>;
  profile: UserProfile;
}) => {
  const siteId = +route.match.params.siteId;
  const isNavOpen = useSelector(getIsNavOpen);
  const doCloseNav = useAction(closeNav);
  const doToggleNav = useAction(toggleNav);
  const classes = useStyles();
  const siteContext = useSelector(getSiteContext);
  const showCommands = siteContext.dataPartnerId === FORTRESS_GUARDIAN;
  const dispatch = useDispatch();
  const theme = useTheme();

  const isMobilePortrait = useMediaQuery(
    `${theme.breakpoints.down('xs')} and (orientation: portrait) and (hover: none)`
  );
  const isMobileLandscape = useMediaQuery(
    `${theme.breakpoints.down('sm')} and (orientation: landscape) and (hover: none)`
  );
  const isMobileScreen = isMobilePortrait || isMobileLandscape;
  const [showMobileDialog, setShowMobileDialog] = useState(
    sessionStorage.getItem('mobile-dialog') !== 'closed'
  );

  useEffect(() => {
    dispatch(getPoints.request(siteId, true));
  }, []);

  return (
    <>
      <div className={classes.root}>
        <MainNavigation
          profile={profile}
          isNavOpen={isNavOpen}
          closeNav={doCloseNav}
          toggleNav={doToggleNav}
        />

        <main className={classes.content}>
          <TimeTravelBanner className={classes.timeTravel} />
          <MainToolbar profile={profile} siteId={siteId} toggleNav={doToggleNav} />

          <div className={classes.contentOuter}>
            <div className={classes.contentInner}>
              <Switch>
                <Route path={PathPattern.SITE} exact>
                  {ismVoult ? (
                    <Redirect
                      to={{
                        pathname: PathBuilder.OPERATIONAL_SUMMARY(siteId),
                      }}
                    />
                  ) : (
                    <DashboardPageLoader />
                  )}
                </Route>

                <Route path={PathPattern.ANALYSIS} exact>
                  <AnalysisPageLoader />
                </Route>

                <Route path={PathPattern.ALARMS_DEFAULT}>
                  <AlarmsPageLoader />
                </Route>

                <Route path={PathPattern.OPERATIONAL_SUMMARY} exact>
                  <OpSummaryPageLoader />
                </Route>

                {featureFlags.enableControl && (
                  <Route path={PathPattern.CONTROL_SUMMARY} exact>
                    <ControlSummaryPageLoader />
                  </Route>
                )}

                {featureFlags.enableReports && (
                  <Route path={PathPattern.REPORT} exact>
                    <AllowForRoles
                      siteId={siteId}
                      systemAdmin
                      customerAdmin
                      roles={[SiteRole.SITE_ADMIN]}
                      renderMessage={NotAuthorized}
                    >
                      <ReportsPageLoader />
                    </AllowForRoles>
                  </Route>
                )}

                {featureFlags.enableSettings && (
                  <Route path={PathPattern.SETTINGS} exact>
                    <AllowForRoles
                      siteId={siteId}
                      roles={[SiteRole.SITE_ADMIN]}
                      renderMessage={NotAuthorized}
                    >
                      <Settings siteId={siteId} />
                    </AllowForRoles>
                  </Route>
                )}

                {featureFlags.enableCommands && showCommands && (
                  <Route path={PathPattern.COMMANDS} exact>
                    <Commands />
                  </Route>
                )}

                <Route path={PathPattern.POINTS} exact>
                  <PointsPageLoader />
                </Route>

                <Route path={PathPattern.EQUIPMENT} exact>
                  <EquipmentPageLoader />
                </Route>

                <Route path={PathPattern.ALARM_NOTIFICATIONS} exact>
                  <AllowForRoles
                    siteId={siteId}
                    systemAdmin
                    customerAdmin
                    roles={[SiteRole.SITE_ADMIN]}
                    renderMessage={profile.isSystemAdmin ? SameCustomerMessage : NotAuthorized}
                  >
                    <AlertPageLoader />
                  </AllowForRoles>
                </Route>

                <Route path={PathPattern.USER_MANAGEMENT} exact>
                  <AllowForRoles
                    siteId={siteId}
                    systemAdmin
                    customerAdmin
                    installer
                    roles={[SiteRole.SITE_ADMIN]}
                    renderMessage={NotAuthorized}
                  >
                    <UserPageLoader />
                  </AllowForRoles>
                </Route>

                {featureFlags.enableInstaller && (
                  <Route path={PathPattern.INSTALLER} exact>
                    <AllowForRoles
                      siteId={siteId}
                      systemAdmin
                      customerAdmin
                      installer
                      renderMessage={NotAuthorized}
                    >
                      <InstallerPageLoader />
                    </AllowForRoles>
                  </Route>
                )}

                <Route>
                  <Typography>Page Not Found</Typography>
                </Route>
              </Switch>
            </div>
          </div>
        </main>
      </div>
      {isMobileScreen && ismVoult && (
        <Dialog
          classes={{ paper: classes.dialog }}
          open={showMobileDialog}
          transitionDuration={0}
          fullScreen
        >
          <img src={mvoultLogo} style={{ width: 300 }} alt="Logo" />
          <DialogTitle disableTypography>
            <Typography variant="h5">Download the mVoult mobile app!</Typography>
          </DialogTitle>
          <Typography gutterBottom>
            The best mobile experience for mVoult users is through the mVoult mobile app. Please
            download it using one of the links below.
          </Typography>
          {/* will be replaced by app store buttons */}
          <DialogActions>
            <div>
              <PrimaryButton>iPhone</PrimaryButton>
            </div>
            <div>
              <PrimaryButton>Android</PrimaryButton>
            </div>
          </DialogActions>
          <DialogActions>
            <Box marginTop={2}>
              <SecondaryButton
                onClick={() => {
                  setShowMobileDialog(false);
                  sessionStorage.setItem('mobile-dialog', 'closed');
                }}
              >
                Continue With Browser
              </SecondaryButton>
            </Box>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

export default SiteRoute;
